<template>
  <div
    ref="tree"
    class="ten-tree"
    v-on="$listeners"
    v-show="inited"
  >
    <slot>
      <ten-tree-node
        v-for="item in treeData"
        :key="getKey(item)"
        :data="item"
        :selectable="selectable"
        :show-checkbox="multiple && multipleCheckbox"
        :level-index="levelIndex"
      />
    </slot>
  </div>
</template>

<script>
import TreeModel from './tree-model';
import TenTreeNode from './tree-node';
import { isEmpty } from '../scripts/utils/helper';
import cloneDeep from 'lodash/cloneDeep';

const name = 'ten-tree';

const LEVEL_INDEX = 0;

export default {
  name,
  components: {
    TenTreeNode,
  },
  mixins: [],
  provide() {
    return {
      tree: this,
      treeNodeParent: this,
    };
  },
  props: {
    /**
     * data 树形数据
     */
    data: {
      type: Array,
      default: () => [],
    },
    /**
     * 是否可选
     */
    selectable: Boolean,
    /**
     * @ignore
     * 选择模式，设置为 independent 则各个节点独立可选
     * @member default | independent
     */
    selectMode: {
      type: String,
      default: 'default',
    },
    /**
     * v-model 当前选中值，对应事件 selectedChange
     */
    selected: [Number, String, Object, Array],
    /**
     * 当前选中值
     */
    defaultSelected: [Number, String, Object, Array],
    /**
     * 当前展开节点的 keys，支持 .sync 修饰符，对应事件 update:expanded
     */
    expanded: {
      type: Array,
      default: () => [],
    },
    /**
     * 是否自动展开父节点
     */
    expandParent: {
      type: Boolean,
      default: true,
    },
    /**
     * 是否自动折叠兄弟菜单
     */
    closeSiblings: Boolean,
    /**
     * 当前默认展开节点的 keys
     */
    defaultExpanded: Array,
    /**
     * 默认展开所有节点
     */
    defaultExpandAll: Boolean,
    /**
     * 选中值类型 key or node data（异步获取选项时适用）
     * @member key | data
     */
    selectedType: String,
    /**
     * 定义选中 keys 的形式，会决定 onChange 接受到的 selectedKeys 数组的形式<br>
     * all: 选中数组包含所有选中的节点 <br>
     * onlyLeaf: 选中数组包含所有叶子的节点 <br>
     * parentFirst: 若节点被选中，则不包含该节点的子孙节点
     * @member  all | onlyLeaf | parentFirst
     */
    selectedMode: {
      type: String,
      default: 'all',
    },
    /**
     * 是否多选
     */
    multiple: Boolean,
    /**
     * 多选模式下是否显示 checkbox
     */
    multipleCheckbox: {
      type: Boolean,
      default: true,
    },
    /**
     * 节点 key(id) 在 nodeData 中对应的键名
     */
    nodeKey: {
      type: String,
      default: 'key',
    },
    /**
     * 节点文本在 nodeData 中对应的键名
     */
    nodeText: {
      type: String,
      default: 'text',
    },
    /**
     * 自定义渲染节点内容
     * Function(h, nodeData)
     */
    renderNodeContent: Function,
    /**
     * 节点 children 在 nodeData 中对应的键名
     */
    nodeChildren: {
      type: String,
      default: 'children',
    },
    /**
     * 加载子节点
     */
    loadChildren: Function,
    /**
     * 是否可拖拽
     */
    draggable: Boolean,
    /**
     * 拖拽放置钩子，在某节点之前插入节点，若返回 false 则阻止拖拽
     * (sourceNodeData, targetNodeData)
     */
    onNodeDropInsertBefore: Function,
    /**
     * 拖拽放置钩子，在某节点之后插入节点，若返回 false 则阻止拖拽
     * (sourceNodeData, targetNodeData)
     */
    onNodeDropInsertAfter: Function,
    /**
     * 拖拽放置钩子，在某节点之内加入节点，若返回 false 则阻止拖拽
     * (sourceNodeData, targetNodeData)
     */
    onNodeDropAppend: Function,
  },
  data() {
    return {
      treeData: this.$props.data,
      inited: false,
      levelIndex: LEVEL_INDEX,
    };
  },
  computed: {
    _dataSelected() {
      return this.transform(this.selected);
    },
  },
  watch: {
    _dataSelected: {
      handler(v) {
        this.nodeModel.setSelected(v);
        this.nodeModel.applySelected();

        this.updateDataKeyMap();
      },
    },
    expanded: {
      handler(v) {
        this.nodeModel.setExpanded(this.expanded);
        this.nodeModel.applyExpanded(v);
      },
    },
    data: {
      handler(v) {
        this.treeData = cloneDeep(v);
        this.updateDataKeyMap();
      },
      immediate: true,
    },
    treeData() {
      this.nodeModel.update();
    },
  },
  beforeCreate() {
    this.nodeModel = new TreeModel();
  },
  created() {
    this.nodeModel.setConfig({
      selectMode: this.selectMode,
      expandParent: this.expandParent,
      closeSiblings: this.closeSiblings,
    });
    this.nodeModel.setSelected(this._dataSelected);

    let { expanded } = this;
    if (
      !('expanded' in this.$vnode.componentOptions.propsData)
      && this.defaultExpandAll
    ) {
      expanded = Object.keys(this.dataKeyMap);
    }
    this.nodeModel.setExpanded(expanded);

    this.nodeModel.update();
    this.draggingSourceModelId = null;
  },
  mounted() {
    this.$refs.tree.classList.add('ten-tree--uninitialized');
    setTimeout(() => {
      this.inited = true;
    });
    setTimeout(() => {
      this.$refs.tree.classList.remove('ten-tree--uninitialized');
    }, 300);
  },
  methods: {
    /**
     * @public
     * 获取当前数据，tree 会在内部维护一份 data 拷贝，在拖拽触发 data 变化之后，可以通过此方法获取当前的 data
     */
    getCurrentData() {
      return cloneDeep(this.treeData);
    },
    transform(v) {
      let dataSelected;
      if (!this.multiple) {
        dataSelected = !isEmpty(v) ? [v] : [];
      } else {
        dataSelected = Array.isArray(v) ? v : [];
      }
      if (this.selectedType === 'data') {
        dataSelected = dataSelected.map((data) => {
          const key = this.getKey(data);
          return key;
        });
      }
      return dataSelected;
    },
    reverse(v) {
      let propSelected = v;
      if (this.selectedType === 'data') {
        propSelected = v.map(key => this.getDataByKey(key));
      }
      if (!this.multiple) {
        propSelected = propSelected.length ? propSelected[0] : undefined;
      }
      return propSelected;
    },
    updateDataKeyMap() {
      this.treeDataKeyMap = this.getDataKeyMap(this.treeData);
      this.selectedDataKeyMap = {};
      if (this.selectedType === 'data' && this.selected) {
        const selected = this.multiple ? this.selected : [this.selected];
        selected.forEach((node) => {
          const key = this.getKey(node);
          this.selectedDataKeyMap[key] = node;
        });
      }
      this.dataKeyMap = { ...this.treeDataKeyMap, ...this.selectedDataKeyMap };
    },
    getDataKeyMap(data) {
      const keyMap = {};
      this.walkData(data, (node) => {
        const key = this.getKey(node);
        keyMap[key] = node;
      });
      return keyMap;
    },
    getKey(nodeData) {
      return nodeData[this.nodeKey];
    },
    getText(nodeData) {
      return nodeData[this.nodeText];
    },
    renderTreeNodeContent(h, nodeData) {
      if (this.renderNodeContent) {
        return this.renderNodeContent(h, { data: nodeData });
      } if (this.$scopedSlots.nodeContent) {
        return this.$scopedSlots.nodeContent({ data: nodeData });
      }
      return this._v(`${this.getText(nodeData)}`);
    },
    getChildren(nodeData) {
      return nodeData[this.nodeChildren];
    },
    setChildren(nodeData, arr) {
      // eslint-disable-next-line
      nodeData[this.nodeChildren] = arr;
      this.$nextTick(() => {
        this.nodeModel.setSelected(this._dataSelected);
        this.nodeModel.applySelected();
      });
      this.updateDataKeyMap();
    },
    walkData(data, fn) {
      if (Array.isArray(data)) {
        data.forEach(item => this.walkData(item, fn));
      } else {
        fn(data);
        const children = this.getChildren(data);
        if (children) {
          this.walkData(children, fn);
        }
      }
    },
    getDataByKey(key) {
      return this.dataKeyMap[key];
    },
    onNodeSelectChange(key, selected) {
      let selectedKeys;
      if (!this.multiple) {
        selectedKeys = [key];
      } else {
        this.nodeModel.handleSelectedChange(key, selected);
        const unSortedSelected = Array.prototype.concat.apply(
          [],
          this.nodeModel.children.map(child => child.getSelected(this.selectedMode))
        );
        // 排序
        selectedKeys = [];
        this._dataSelected.forEach((preSelectedKey) => {
          const index = unSortedSelected.indexOf(preSelectedKey);
          if (index > -1) {
            selectedKeys.push(preSelectedKey);
            unSortedSelected.splice(index, 1);
          }
          if (this.selectedDataKeyMap[preSelectedKey] && !this.treeDataKeyMap[preSelectedKey]) {
            selectedKeys.push(preSelectedKey);
          }
        });
        selectedKeys = selectedKeys.concat(unSortedSelected);
      }
      /**
       * 选中值变化事件
       * @param selected 选中值
       */
      this.$emit('selectedChange', this.reverse(selectedKeys));
    },
    onNodeExpandChange(key, expanded) {
      this.nodeModel.handleExpandedChange(key, expanded);

      const expandedKeys = Array.prototype.concat.apply(
        [],
        this.nodeModel.children.map(child => child.getExpanded())
      );
      /**
       * 节点展开事件
       * @param {Array} expandedKeys 展开节点 keys
       */
      this.$emit('update:expanded', expandedKeys);
    },

    /* 节点拖拽 */
    onNodeDragStart(sourceModelId, e) {
      const sourceNode = this.nodeModel.getNodeById(sourceModelId);
      /**
       * 节点开始拖拽事件
       * @param {Object} data 被拖拽的节点
       * @param {Object} event 拖拽事件对象
       */
      this.$emit('nodeDragStart', sourceNode.data, e);
    },
    onNodeDragEnter(sourceModelId, targetModelId, e) {
      const targetNode = this.nodeModel.getNodeById(targetModelId);
      const sourceNode = this.nodeModel.getNodeById(sourceModelId);
      /**
       * 拖拽进入其他节点时触发的事件
       * @param {Object} sourceData 被拖拽的节点
       * @param {Object} targetData 目标节点
       * @param {Object} event 拖拽事件对象
       */
      this.$emit('nodeDragEnter', sourceNode.data, targetNode.data, e);
    },
    onNodeDragOver(sourceModelId, targetModelId, e) {
      const targetNode = this.nodeModel.getNodeById(targetModelId);
      const sourceNode = this.nodeModel.getNodeById(sourceModelId);
      /**
       * 拖拽节点悬浮于其他节点时触发的事件
       * @param {Object} sourceData 被拖拽的节点
       * @param {Object} targetData 目标节点
       * @param {Object} event 拖拽事件对象
       */
      this.$emit('nodeDragOver', sourceNode.data, targetNode.data, e);
    },
    onNodeDragLeave(sourceModelId, targetModelId, e) {
      const targetNode = this.nodeModel.getNodeById(targetModelId);
      const sourceNode = this.nodeModel.getNodeById(sourceModelId);
      /**
       * 拖拽节点离开其他节点时触发的事件
       * @param {Object} sourceData 被拖拽的节点
       * @param {Object} targetData 目标节点
       * @param {Object} event 拖拽事件对象
       */
      this.$emit('nodeDragLeave', sourceNode.data, targetNode.data, e);
    },
    onNodeDragEnd(sourceNode, e) {
      /**
       * 拖拽结束时触发的事件
       * @param {Object} sourceData 被拖拽的节点
       * @param {Object} event 拖拽事件对象
       */
      this.$emit('nodeDragEnd', sourceNode.data, e);
    },
    onNodeDrop(sourceModelId, targetModelId, e) {
      const targetNode = this.nodeModel.getNodeById(targetModelId);
      const sourceNode = this.nodeModel.getNodeById(sourceModelId);
      /**
       * 拖拽释放于其他节点时触发的事件
       * @param {Object} sourceData 被拖拽的节点
       * @param {Object} targetData 目标节点
       * @param {Object} event 拖拽事件对象
       */
      this.$emit('nodeDrop', sourceNode.data, targetNode.data, e);
    },
    insert(beforeOrAfter = 0, sourceModelId, targetModelId) {
      const targetNode = this.nodeModel.getNodeById(targetModelId);
      const sourceNode = this.nodeModel.getNodeById(sourceModelId);

      if (!(sourceNode && targetNode)) {
        return;
      }

      if (beforeOrAfter === 0 && this.onNodeDropInsertBefore) {
        if (
          this.onNodeDropInsertBefore(sourceNode.data, targetNode.data)
          === false
        ) {
          return;
        }
      }

      if (beforeOrAfter === 1 && this.onNodeDropInsertAfter) {
        if (
          this.onNodeDropInsertAfter(sourceNode.data, targetNode.data) === false
        ) {
          return;
        }
      }

      const { treeData } = this;

      let dataArr;

      // delete
      if (sourceNode.parent.isRoot) {
        dataArr = treeData;
      } else {
        dataArr = this.getChildren(sourceNode.parent.data);
      }
      const index = dataArr.indexOf(sourceNode.data);
      if (index > -1) {
        dataArr.splice(index, 1);
      }

      // insert
      if (targetNode.parent.isRoot) {
        dataArr = treeData;
      } else {
        dataArr = this.getChildren(targetNode.parent.data);
      }

      const index2 = dataArr.indexOf(targetNode.data);
      if (index2 > -1) {
        dataArr.splice(index2 + beforeOrAfter, 0, sourceNode.data);
      }

      const newTreeData = cloneDeep(treeData);
      this.treeData = newTreeData;
      this.$emit('dataChange', newTreeData);
    },
    append(sourceModelId, targetModelId) {
      const targetNode = this.nodeModel.getNodeById(targetModelId);
      const sourceNode = this.nodeModel.getNodeById(sourceModelId);

      if (!(sourceNode && targetNode)) {
        return;
      }

      if (this.onNodeDropAppend) {
        if (this.onNodeDropAppend(sourceNode.data, targetNode.data) === false) {
          return;
        }
      }

      const { treeData } = this;

      let dataArr;

      // delete
      if (sourceNode.parent.isRoot) {
        dataArr = treeData;
      } else {
        dataArr = this.getChildren(sourceNode.parent.data);
      }
      const index = dataArr.indexOf(sourceNode.data);
      if (index > -1) {
        dataArr.splice(index, 1);
      }

      // append
      let children = this.getChildren(targetNode.data);
      if (!Array.isArray(children)) {
        children = [];
        targetNode.data[this.nodeChildren] = children;
      }
      children.push(sourceNode.data);
      this.treeData = cloneDeep(treeData);
      const newTreeData = cloneDeep(treeData);
      this.treeData = newTreeData;
      /**
       * data 发生变化，一般发生在拖拽之后
       * @param {Array} newTreeData
       */
      this.$emit('dataChange', newTreeData);
    },
  },
};
</script>
